import Splide from '@splidejs/splide'
import { Controller } from 'stimulus'
import { getCsrfParamsAsJSON } from '~/common/helpers/csrf'
import { haptics } from '~/common/helpers/haptics'
import {
  LIGHTBOX_CLOSED,
  LIGHTBOX_COLLAPSED,
  LIGHTBOX_EXPANDED,
  LIGHTBOX_OPENED,
  announceToEmbedder,
  embedderWindow,
} from '~/common/helpers/framed_lightbox'
import SimpleroPusher from '~/common/helpers/simplero_pusher'
import { findPreviewListIndex } from '../preview_list_index'

import '@splidejs/splide/css'
import './slider_component.scss'

export default class extends Controller {
  static values = {
    height: Number,
  }

  static targets = [
    'preview',
    'splideElTemplate',
    'splideElThumbnailTemplate',
    'splideThumbnail',
    'splidePreview',
  ]

  initializeSplide(target, options) {
    const splide = new Splide(target, options)
    splide.mount()
    return splide
  }

  connect() {
    if (this.hasSplideThumbnailTarget) {
      this.thumbnailSplide = this.initializeSplide(this.splideThumbnailTarget, {
        autoWidth: true,
        fixedHeight: `${this.heightValue}px`,
        perMove: 1,
        type: 'slide',
        gap: 15,
        pagination: false,
        isOverflow: true,
      })
    }

    this.previewSplide = this.initializeSplide(this.splidePreviewTarget, {
      type: 'slide',
      perPage: 1,
      perMove: 1,
      pagination: false,
      keyboard: 'global',
    })

    // Detent tick when the full-screen gallery swipes to a new image.
    this.previewSplide.on('move', () => haptics.tick())

    if (embedderWindow()) {
      window.addEventListener('message', this.handleEmbedderMessage)
    }
  }

  // Only the embedder that framed us, and only to say whether it made room.
  handleEmbedderMessage = (event) => {
    if (event.source !== embedderWindow()) return

    if (event.data?.type === LIGHTBOX_EXPANDED) {
      document.documentElement.classList.add('framed-lightbox-expanded')
    } else if (event.data?.type === LIGHTBOX_COLLAPSED) {
      document.documentElement.classList.remove('framed-lightbox-expanded')
    }
  }

  disconnect() {
    // Never leave the chat sidebar stretched over the call because the message
    // this preview belonged to was re-rendered while it was open.
    announceToEmbedder(LIGHTBOX_CLOSED)
    this.previewSplide?.destroy(true)
    this.thumbnailSplide?.destroy(true)
  }

  addItem(event) {
    const { thumbnail, preview, index, objectId, pusherChannel } = event.detail

    let { objectName } = event.detail
    if (!objectName) {
      objectName = 'asset'
    }

    const createSlide = (innerHTML, isPreview = false) => {
      const templateTarget = isPreview
        ? this.splideElTemplateTarget
        : this.splideElThumbnailTemplateTarget
      const slide = templateTarget.content.cloneNode(true).firstElementChild

      if (objectId) {
        Object.keys(slide.dataset).forEach((key) => {
          if (slide.dataset[key] === '__OBJECT_ID__') {
            slide.dataset[key] = `${objectName}-${objectId}`
          }
        })
      }

      slide.innerHTML = innerHTML
      return slide
    }

    const thumbnailSlide = createSlide(thumbnail)
    this.thumbnailSplide.add(thumbnailSlide, index)

    if (preview) {
      this.previewSplide.add(createSlide(preview, true), index)
    }

    if (pusherChannel) {
      this.listenToChannel(pusherChannel, {
        element: thumbnailSlide,
        index: index,
      })
    }
  }

  removeItem(event) {
    let {
      detail: { index },
    } = event
    this.thumbnailSplide.remove(index)
  }

  findAndRemoveItem(id) {
    const index = this.getIndexOfLi(`#js-preview-thumbnail-${id}`)
    if (index === null) return
    this.removeItem({ detail: { index } })
  }

  getIndexOfLi(selector) {
    const ulEl = this.splideThumbnailTarget.querySelector('ul')
    return findPreviewListIndex(ulEl, selector)
  }

  openPreviewSplide(event) {
    const {
      detail: { index },
    } = event

    if (this.rightSidebarElement()) {
      this.rightSidebarElement().style.zIndex = 20
    }

    if (!this._originalViewportContent) {
      this._originalViewportContent = this.#viewportMeta().content
      this.#viewportMeta().content = this.#viewportMeta().content.replaceAll(
        /maximum-scale=[\d.]*,?\s+/g,
        ''
      )
    }

    announceToEmbedder(LIGHTBOX_OPENED)
    this.previewTarget.classList.remove('hidden')
    this.previewSplide.go(index)
    this.previewSplide.refresh()
  }

  #viewportMeta() {
    return document.querySelector('meta[name=viewport]')
  }

  openFullPreview(e) {
    const target = e.target
    if (
      ['js-close-preview', 'js-download-link'].some(
        (cls) =>
          target.classList.contains(cls) ||
          target.parentElement.classList.contains(cls)
      )
    ) {
      return
    }

    const objectId = e.params.objectId
    const index = this.getIndexOfLi(`#js-preview-thumbnail-${objectId}`)
    if (index === null) return
    this.openPreviewSplide({ detail: { index: index } })
  }

  closeFullPreview(e) {
    if (this.anyAssetTarget(e.target) || this.downloadLink(e.target)) {
      return
    }
    if (this._originalViewportContent) {
      this.#viewportMeta().content = this._originalViewportContent
      this._originalViewportContent = null
    }
    this.stopVideo()
    this.previewTarget.classList.add('hidden')
    announceToEmbedder(LIGHTBOX_CLOSED)
    if (this.rightSidebarElement()) {
      this.rightSidebarElement().style.zIndex = ''
    }
  }

  stopVideo() {
    // Stop Simplero video players
    this.previewTarget.querySelectorAll('.video-js').forEach((video) => {
      window.videojs?.getPlayer(video.id)?.pause()
    })

    // Stop iframe video players
    const iframes = this.previewTarget.querySelectorAll('iframe')
    iframes.forEach((iframe) => {
      // Reload the src, which will make it stop
      // For some reason setting the src only doesn't work so setting it to 'about:blank' first and then setting it back to the original src
      const src = iframe.src
      iframe.src = 'about:blank'
      setTimeout(function () {
        iframe.src = src
      }, 10)
    })
  }

  anyAssetTarget(target) {
    return (
      target.closest('.audio-wrapper') ||
      target.closest('.video-wrapper') ||
      target.tagName === 'IMG'
    )
  }

  downloadLink(target) {
    return (
      target.classList.contains('js-download-link') ||
      target.closest('.js-download-link')
    )
  }

  closeFullPreviewFromKeyboardShortcut(e) {
    e.preventDefault()
    this.closeFullPreview(e)
  }

  destroyObject(event) {
    const self = this
    const objectType = event.params.objectType

    const parentEl = event.target.closest('.js-thumbnail')
    parentEl.style.opacity = 0.5
    parentEl.classList.add('js-deleting-attachment')
    const closePreviewBtn = parentEl.querySelector('.js-close-preview')
    closePreviewBtn.classList.add('hidden')
    const loaderBtn = parentEl.querySelector('.js-delete-btn-loader')
    loaderBtn.classList.remove('hidden')
    const dropzoneContainer = parentEl.closest('.js-dropzone-container')
    this.dispatchToggleFormSubmitBtn(dropzoneContainer, true)

    fetch(event.params.url, {
      method: 'DELETE',
      body: getCsrfParamsAsJSON(),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json()
        } else {
          return response.json().then((json) => {
            this.deleteBtnLoaderTarget.classList.add('hidden')
            closePreviewBtn.classList.remove('hidden')
            parentEl.classList.remove('js-deleting-attachment')
            this.dispatchToggleFormSubmitBtn(dropzoneContainer, false)
            throw new Error(json.error)
          })
        }
      })
      .then((response) => {
        self.findAndRemoveItem(`${objectType}-${response.id}`)
        self.updateInputValue(objectType, response.id)
        this.dispatchToggleFormSubmitBtn(dropzoneContainer, false)
        dropzoneContainer.dispatchEvent(
          new CustomEvent('assets-changed', { bubbles: true })
        )
      })
      .catch((error) => alert(error))
  }

  dispatchToggleFormSubmitBtn(element, disable) {
    element.dispatchEvent(
      new CustomEvent('dropzone:form-submit-toggle', {
        detail: { disable: disable },
        bubbles: true,
      })
    )
  }

  // There are two hidden input fields embed_ids and asset_ids so when an asset or embed is deleted
  // remove its id from the input field
  updateInputValue(objectType, id) {
    const inputEl = this.element
      .closest('form')
      .querySelector(`input[id$="${objectType}_ids_"]`)
    let inputVal = inputEl.value.split(',')
    const index = inputVal.indexOf(id.toString())
    if (index >= 0) {
      inputVal.splice(index, 1)
    }
    inputEl.value = inputVal.join(',')
  }

  connectToChannel(event) {
    const { channel, elementDetails } = event.detail
    this.listenToChannel(channel, elementDetails)
  }

  listenToChannel(channel, elementDetails) {
    SimpleroPusher.bind(channel, 'conversion_progress', (data) =>
      this.onUpdate(data, elementDetails)
    )
  }

  onUpdate = (data, elementDetails) => {
    if (data.conversion_state === 'processing' && data.conversion_percent) {
      this.updateProgressBar(data.conversion_percent, elementDetails)
    }

    if (data.conversion_state === 'finished' && data.asset_id) {
      this.onConversionFinished(data, elementDetails)
    }
  }

  updateProgressBar = (conversionPercentage, elementDetails) => {
    const progressBarEl =
      elementDetails.element.querySelector('.js-progress-bar')
    progressBarEl.style.width = `${conversionPercentage}%`
  }

  onConversionFinished = (data, elementDetails) => {
    if (!data.thumbnail_preview) return

    const index = elementDetails.index
    this.removeItem({
      detail: {
        index: index,
      },
    })
    this.addItem({
      detail: {
        thumbnail: data.thumbnail_preview,
        preview: data.preview,
        index: index,
        objectId: data.asset_id,
      },
    })
  }

  rightSidebarElement() {
    return this.element.closest('.js-layout-right-sidebar')
  }

  removeAllSlides = () => {
    if (this.hasSplideThumbnailTarget) {
      this.thumbnailSplide.remove((slide) => slide.index >= 0)
    }
    this.previewSplide.remove((slide) => slide.index >= 0)
  }
}
